import { v4 as uuidV4 } from 'uuid';

import { useDebounceFn } from '@vueuse/core';
import type { Maybe } from '@/types/GraphQL';
import { useCart } from '~/composables/custom/useCart/useCart';
import { useUnzer } from '~/composables/custom/useUnzer/useUnzer';
import { useTracking } from '~/composables/custom/useTracking';
import { useUser } from '~/composables/custom/useUser/useUser';
import useUiNotification from '~/composables/useUiNotification';
import type { UiNotification } from '~/composables/useUiNotification';
import { useBFF } from '~/composables/useBFF/useBFF';
import { userGetters } from '~/composables/getters';
import { getErrorMessage } from '~/helpers/utils/getErrorMessage';

export interface PaymentMethod {
  name: string;
  text: string;
  label: string;
  enabled: boolean;
  minThreshold: unknown;
  failedConditions: string[];
  failedConditionsMessages: {
    minThreshold: unknown;
    shippingIsBilling: unknown;
    customerIsBilling: unknown;
    clickAndCollectPayment: unknown;
  };
  logo: string | undefined;
  provider: string;
  method: string;
}

interface AddPaymentRequest {
  orderNumber: string | undefined;
  name: string;
  method: string;
  provider: string;
  birthDate?: string;
}

interface CreateOrderRequest {
  email?: Maybe<string> | undefined;
  threatMetrixId?: string;
  resourceId?: string;
  cardDetails?: CardDetails;
}
interface CardDetails {
  number: string;
  brand: string;
  expiryDate: string;
  cardHolder: string;
}

type PaymentErrorTypes = 'addPayment' | 'createPaymentOrder' | 'loadPayments' | 'addPaymentOption' | 'checkCreditCardCall';

interface CreateOrderErrors {
  error: 'cartValidationError' | 'cartIsOrdered' | 'cartIsNotActive';
  errors: {
    errorCode: 'priceMismatchAfterRecalculate';
  }[];
};

const invoiceThreatMetrixSessionId = ref<string>();
const loading = ref(false);
const availablePaymentOptions = shallowRef<PaymentMethod[] | any[]>();
const paymentCreditCard = ref<any>();
const paymentApplePay = ref<any>();
const paymentGooglePay = ref<any>();

export const usePayment = () => {
  const { $checkout, $carts } = useBFF();
  const { $i18n, $config, $domain } = useNuxtApp();
  const {
    cart,
    setCart,
    refresh,
    recalculateCart,
  } = useCart();
  const { send } = useUiNotification();
  const {
    errors: unzerError,
    initializeUnzerPayment,
  } = useUnzer();
  const { trackError, trackAddPaymentInfo } = useTracking();
  const { user } = useUser();
  const isPaymentInAdvanceBlocked = computed(() => userGetters.isPaymentInAdvanceBlocked(user.value));
  const paymentProviderRedirectUrl = ref<string | undefined>();
  const paymentInCart = shallowRef<unknown>();
  const errors = shallowRef<Record<PaymentErrorTypes, any>>({
    loadPayments: undefined,
    addPaymentOption: undefined,
    addPayment: undefined,
    createPaymentOrder: undefined,
    checkCreditCardCall: undefined,
  });

  const generateThreatMetrixSessionId = () => {
    invoiceThreatMetrixSessionId.value = `kik_${uuidV4()}-${Date.now()}`;

    return invoiceThreatMetrixSessionId.value;
  };

  const loadPayments = async () => {
    try {
      loading.value = true;
      const orderNumber = cart.value?.key ?? undefined;
      const { data } = await $checkout.getPaymentMethods(orderNumber);

      if (!orderNumber) { // no order number provided means OPS will generate one and cart version got bumped
        await refresh();
      }

      availablePaymentOptions.value = transformFailedConditions(data);
    }
    catch (error: unknown) {
      if (error instanceof Error) {
        errors.value.loadPayments = error.message;
      }
      trackError('payments', errors.value.loadPayments);
    }
    finally {
      loading.value = false;
    }

    return undefined;
  };

  const debounceAddPaymentOption = useDebounceFn((selectedPayment: PaymentMethod, birthDate?: string) => {
    addPaymentOption(selectedPayment, birthDate);
  }, 500);

  const addPaymentOption = async (selectedPayment: PaymentMethod, birthDate?: string) => {
    try {
      loading.value = true;
      errors.value.addPayment = undefined;
      errors.value.addPaymentOption = undefined;

      const {
        name,
        method,
        provider,
      } = selectedPayment;
      const orderNumber = cart.value?.key ?? undefined;

      const payload: AddPaymentRequest = {
        name,
        orderNumber,
        birthDate,
        method,
        provider,
      };
      await recalculateCart();

      const { data } = await $carts.setPayment(cart.value?.id, cart.value?.version, orderNumber, payload);

      await setCart({ ...cart.value, ...data });
      if (!data.paymentInfo) {
        throw Error();
      }

      paymentInCart.value = data.paymentInfo;
      trackAddPaymentInfo(payload.method);
    }
    catch (error: any) {
      if (error instanceof Error) {
        sendError('somethingWentWrong');
      }

      if (error.error === 'cartIsOrdered' || error.error === 'cartIsNotActive') {
        errors.value.addPaymentOption = error.message;
        sendError(getErrorMessage(error.error), { ttlInMs: 10000 });
        navigateTo({ name: 'thank-you', query: { orderNumber: cart.value?.key } });

        return;
      }

      errors.value.addPaymentOption = error.message;
      trackError('payments', errors.value.addPaymentOption);
    }
    finally {
      loading.value = false;
    }
  };

  const createPaymentOrder = async (paymentOption?: Pick<PaymentMethod, 'method' | 'provider'>, { isPaypalExpress = false }: { isPaypalExpress?: boolean } = { isPaypalExpress: false }) => {
    const { UNZER: unzerProvider, UNZER_ECOM: unzerEcomProvider } = $config.public.payment.providers;
    paymentProviderRedirectUrl.value = undefined;
    errors.value.createPaymentOrder = undefined;
    try {
      loading.value = true;
      const resourceId = await initialize(paymentOption);
      if (!resourceId && [unzerProvider, unzerEcomProvider].includes(paymentOption?.provider)) {
        throw new Error('Missing resourceId');
      }
      // Temporary solution for PayPal Express in Poland
      const paypalPaymentProvider = $domain.countryCode === 'PL' ? $config.public.payment?.providers.PAYPAL_ECOM : $config.public.payment?.providers.PAYPAL;

      const payload: CreateOrderRequest = {
        threatMetrixId: invoiceThreatMetrixSessionId.value,
        resourceId,
        ...(isPaypalExpress ? { paymentMethodName: `${paypalPaymentProvider}-${$config.public.payment?.methods.PAYPAL_EXPRESS}` } : {}),
      };

      const { data } = await $checkout.createOrder(cart.value?.key as string, payload);

      if (data?.redirectUrl) {
        paymentProviderRedirectUrl.value = data.redirectUrl;
      }
    }
    catch (err: unknown) {
      if (err instanceof Error) {
        errors.value.createPaymentOrder = err.message || 'An error occured';
        sendError('somethingWentWrong');
        return;
      }

      const error = err as CreateOrderErrors;
      if (error?.error) {
        errors.value.createPaymentOrder = error.error;
        trackError('payments', errors.value.createPaymentOrder);
        if (error.error === 'cartIsOrdered' || error.error === 'cartIsNotActive') {
          sendError(getErrorMessage(error.error), { ttlInMs: 10000 });
          navigateTo({ name: 'thank-you', query: { orderNumber: cart.value?.key } });
          return;
        }
        if (error.error === 'cartValidationError' && error.errors?.length) {
          error.errors.forEach((e) => {
            errors.value.createPaymentOrder = e?.errorCode;
            if (e?.errorCode === 'priceMismatchAfterRecalculate' && error.errors.length > 1) {
              return;
            }
            send({
              message: getErrorMessage(e?.errorCode),
              type: 'warning',
            });
          });
          await refresh();
          return;
        }
        const errorMessage = getErrorMessage(error.error);
        errors.value.createPaymentOrder = error.error;
        sendError(errorMessage, { ttlInMs: 10000 });
        return;
      }
    }
    finally {
      loading.value = false;
    }
  };

  const initialize = async (paymentOption?: Pick<PaymentMethod, 'method' | 'provider'>) => {
    const { UNZER: unzerProvider, UNZER_ECOM: unzerEcomProvider } = $config.public.payment.providers;
    if (![unzerProvider, unzerEcomProvider].includes(paymentOption?.provider)) {
      return undefined;
    }

    try {
      if (!paymentOption?.method) {
        throw new Error('Missing payment method value');
      }

      let resourceId: string | undefined = '';
      switch (paymentOption.method) {
        case $config.public.payment?.methods?.CARD:
          resourceId = paymentCreditCard.value;
          break;
        case $config.public.payment?.methods?.APPLEPAY:
          resourceId = paymentApplePay.value;
          break;
        case $config.public.payment?.methods?.GOOGLEPAY:
          resourceId = paymentGooglePay.value;
          break;
        default:
          resourceId = await initializeUnzerPayment(paymentOption);
          break;
      }

      if (unzerError.value.token || !resourceId) {
        throw new Error('Failed to authorize provider');
      }

      return resourceId;
    }
    catch (err) {
      if (err instanceof Error) {
        errors.value.createPaymentOrder = err.message;
      }
    }
  };

  const transformFailedConditions = (paymentOptions: PaymentMethod[]) => {
    return paymentOptions
      .filter(option => (isPaymentInAdvanceBlocked.value ? option.name !== 'vorkasse-vorkasse' : true))
      .map((paymentMethod) => {
        const { failedConditions = [] } = paymentMethod;
        const failedConditionsMessages = failedConditions.reduce((acc: Record<string, any>, failedCondition) => {
          acc[failedCondition] = conditionMessages.getFailedConditionMessage(paymentMethod, failedCondition);
          return acc;
        }, {});

        return {
          ...paymentMethod,
          failedConditionsMessages,
        };
      });
  };
  const sendError = (message: string, options?: Pick<UiNotification, 'ttlInMs'>) => {
    send({
      ...(options ?? {}),
      message,
      type: 'danger',
    });
  };

  const conditionMessages = {
    minThreshold: {
      getMessage: (paymentMethod: any) => $i18n.t('paymentMethodLimit', {
        amount: paymentMethod?.minThreshold?.value,
        paymentOption: $i18n.t(paymentMethod.name),
      }),
    },
    shippingIsBilling: {
      getMessage: () => $i18n.t('This payment method is only possible for delivery to the billing address'),
    },
    customerIsBilling: {
      getMessage: () => $i18n.t('CUSTOMER_IS_BILLING_FAILED_CONDITION_PLACE_HOLDER_MESSAGE_KEY'),
    },
    clickAndCollectPayment: {
      getMessage: () => $i18n.t('CLICK_AND_COLLECT_PAYMENT_FAILED_CONDITION_PLACE_HOLDER_MESSAGE_KEY'),
    },
    getFailedConditionMessage(paymentMethod: PaymentMethod, failedCondition: string) {
      return conditionMessages[failedCondition]?.getMessage(paymentMethod);
    },

  };

  const setPaymentCreditCard = (payload: any) => {
    paymentCreditCard.value = payload;
  };

  const setPaymentApplePay = (payload: any) => {
    paymentApplePay.value = payload;
  };

  const setPaymentGooglePay = (payload: any) => {
    paymentGooglePay.value = payload;
  };

  return {
    errors: computed(() => errors.value),
    loading: computed(() => loading.value),
    availablePaymentOptions: computed(() => availablePaymentOptions.value),
    paymentInCart: computed(() => paymentInCart.value),
    invoiceThreatMetrixSessionId: computed(() => invoiceThreatMetrixSessionId.value),
    resetError: (key: PaymentErrorTypes) => { errors.value[key] = undefined; },
    loadPayments,
    addPaymentOption,
    debounceAddPaymentOption,
    createPaymentOrder,
    generateThreatMetrixSessionId,
    setPaymentCreditCard,
    setPaymentApplePay,
    setPaymentGooglePay,

    paymentCreditCard,
    paymentApplePay,
    paymentGooglePay,
    redirectUrl: computed(() => paymentProviderRedirectUrl.value),
  };
};
